import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, ShoppingBasket, ImageOff } from "lucide-react";
import { useState, useEffect } from "react";
import { QuantityInput } from "@/components/QuantityInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple image cache for basket items
const basketImageCache = new Map();

function BasketItemImage({ itemId, itemName }) {
  const [imageUrl, setImageUrl] = useState(basketImageCache.get(itemId) || null);
  const [loading, setLoading] = useState(!basketImageCache.has(itemId));
  const [version, setVersion] = useState(0);

  // Invalidate when the admin updates this item's image
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.itemId === itemId) {
        basketImageCache.delete(itemId);
        setImageUrl(null);
        setLoading(true);
        setVersion((v) => v + 1);
      }
    };
    window.addEventListener("menu-image-updated", handler);
    return () => window.removeEventListener("menu-image-updated", handler);
  }, [itemId]);

  useEffect(() => {
    if (basketImageCache.has(itemId)) {
      setImageUrl(basketImageCache.get(itemId));
      setLoading(false);
      return;
    }

    fetch(`${API}/menu/${itemId}/image`)
      .then(res => res.json())
      .then(data => {
        if (data.image_base64) {
          basketImageCache.set(itemId, data.image_base64);
          setImageUrl(data.image_base64);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [itemId, version]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1aa59a] rounded-full animate-spin" />
      </div>
    );
  }

  if (imageUrl) {
    return <img src={imageUrl} alt={itemName} className="w-full h-full object-cover" />;
  }

  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
      <ImageOff className="w-6 h-6" />
    </div>
  );
}

export default function BasketModal({ open, onClose, basket, onUpdateQuantity, onRemove, onCheckout, total }) {
  if (!basket || basket.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBasket className="w-5 h-5 text-[#1aa59a]" />
              Your Basket
            </DialogTitle>
          </DialogHeader>
          <div className="py-12 text-center">
            <ShoppingBasket className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Your basket is empty</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 max-h-[85vh] flex flex-col">
        <DialogHeader className="p-4 sm:p-6 pb-2 sm:pb-4 flex-shrink-0 border-b">
          <DialogTitle className="flex items-center gap-2">
            <ShoppingBasket className="w-5 h-5 text-[#1aa59a]" />
            Your Basket ({basket.length} items)
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3">
          <div className="space-y-3">
            {basket.map((basketItem, index) => {
              const price = basketItem.variant ? basketItem.variant.price : basketItem.item.price;
              const subtotal = price * basketItem.quantity;
              const uniqueKey = `${basketItem.item.id}-${basketItem.variant?.name || 'base'}`;

              return (
                <div key={uniqueKey} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                  {/* Image */}
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    <BasketItemImage itemId={basketItem.item.id} itemName={basketItem.item.name} />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-800 truncate text-sm">{basketItem.item.name}</h4>
                    {basketItem.variant && (
                      <p className="text-xs text-[#1aa59a]">{basketItem.variant.name}</p>
                    )}
                    <p className="text-xs text-gray-500">£{price.toFixed(2)} each</p>

                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between mt-2">
                      <QuantityInput
                        value={basketItem.quantity}
                        onChange={(qty) => onUpdateQuantity(index, qty)}
                        min={0}
                        testIds={{
                          dec: `decrease-qty-${uniqueKey}`,
                          inc: `increase-qty-${uniqueKey}`,
                          input: `qty-input-${uniqueKey}`,
                        }}
                      />
                      <span className="font-semibold text-[#1aa59a] text-sm">£{subtotal.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => onRemove(index)}
                    className="text-gray-400 hover:text-red-500 transition-colors self-start"
                    data-testid={`remove-item-${uniqueKey}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sticky Footer — always visible on mobile */}
        <div className="border-t p-4 sm:p-6 pt-3 sm:pt-4 flex-shrink-0 bg-white">
          <div className="flex justify-between items-center mb-3">
            <span className="text-lg font-semibold text-gray-800">Total</span>
            <span className="text-2xl font-bold text-[#1aa59a]">£{total.toFixed(2)}</span>
          </div>
          <Button
            onClick={onCheckout}
            className="w-full bg-[#1aa59a] hover:bg-[#158a80] text-white py-5 sm:py-6 text-base sm:text-lg rounded-xl"
            data-testid="checkout-button"
          >
            Place Order
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
